import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  AlertCircle,
  ArrowRight,
  ArrowUpRight,
  CalendarCheck2,
  Check,
  ChevronRight,
  CircleCheck,
  ClipboardCheck,
  Clock3,
  CreditCard,
  HeartHandshake,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  MapPin,
  MessageCircle,
  Minus,
  Plus,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLink, AssuranceRow, SectionIntro, ServiceCard } from "./components";
import deepCleanBeforeAfter from "./assets/deep-clean-before-after.jpg";
import cleaningWindow from "./assets/cleaning-window.jpg";
import freshKitchen from "./assets/fresh-kitchen.jpg";
import calmHome from "./assets/calm-home.jpg";
import {
  deepCleanChecklist,
  deepCleanClientMessage,
  extras,
  faqs,
  formatBookingDate,
  formatMoney,
  pricingExtras,
  pricingNotes,
  servicePriceLists,
  saveLocalBooking,
  services,
  standardCleanIncludes,
  tenancyIncludes,
  type ServiceId,
  type LocalBookingRequest,
} from "./data";

export function HomePage() {
  return (
    <>
      <section className="hero">
        <div className="page-shell hero__grid">
          <div className="hero__copy">
            <p className="eyebrow eyebrow--light"><span />Space to breathe</p>
            <h1>A calmer home<br /><i>starts here.</i></h1>
            <p className="hero__lede">Thoughtful home cleaning, with the details shaped around your day and your space.</p>
            <div className="hero__actions">
              <Link className="button button--mint" to="/book">Book a clean <ArrowRight size={17} /></Link>
              <Link className="button button--ghost-light" to="/services">Explore services <ArrowUpRight size={16} /></Link>
            </div>
            <div className="hero__microcopy"><ShieldCheck size={17} />Clear estimates before your visit is confirmed</div>
          </div>

          <div className="hero__scene" role="img" aria-label="A close-up of a spray bottle being used to clean a glass surface" />
        </div>
        <div className="page-shell"><AssuranceRow /></div>
      </section>

      <section className="section section--cream">
        <div className="page-shell">
          <div className="split-heading">
            <SectionIntro
              eyebrow="Made for real life"
              title={<>The help you need,<br /><i>without the fuss.</i></>}
              copy="Pick the kind of reset your home needs today. Your booking starts with a guide price and ends with a clear confirmation."
            />
            <ArrowLink to="/services">See every service</ArrowLink>
          </div>
          <div className="service-grid service-grid--home">
            {services.slice(0, 3).map((service) => <ServiceCard key={service.id} service={service} />)}
          </div>
        </div>
      </section>

      <section className="section section--paper home-work-section">
        <div className="page-shell">
          <div className="split-heading">
            <SectionIntro
              eyebrow="Care you can see"
              title={<>A little more calm,<br /><i>room by room.</i></>}
              copy="From clear windows to a fresh kitchen, thoughtful cleaning makes the everyday feel lighter."
            />
            <ArrowLink to="/services">Explore our services</ArrowLink>
          </div>
          <div className="home-work-grid">
            <article className="home-work-card home-work-card--large">
              <img src={cleaningWindow} alt="A cleaner carefully wiping a bright window." loading="lazy" />
              <div><p>Windows & glass</p><h3>Let the daylight back in.</h3></div>
            </article>
            <article className="home-work-card">
              <img src={freshKitchen} alt="A bright, freshly tidied kitchen." loading="lazy" />
              <div><p>Kitchen refresh</p><h3>Clear surfaces. Clear head.</h3></div>
            </article>
            <article className="home-work-card">
              <img src={calmHome} alt="A calm, welcoming home ready to unwind in." loading="lazy" />
              <div><p>Home reset</p><h3>Space to settle back in.</h3></div>
            </article>
          </div>
          <div className="home-before-after">
            <div className="home-before-after__copy">
              <p className="eyebrow"><span />Before & after</p>
              <h3>See the difference<br /><i>in the details.</i></h3>
              <p>Drag the slider across the kitchen to compare both sides. Every home is different, so tell us where you’d like us to focus.</p>
              <Link className="arrow-link" to="/book">Plan a deep clean <ArrowRight size={16} /></Link>
            </div>
            <BeforeAfterSlider />
          </div>
        </div>
      </section>

      <section className="section section--moss-story">
        <div className="page-shell story-grid">
          <div className="story-illustration" aria-hidden="true">
            <div className="story-illustration__circle" />
            <div className="story-illustration__tag">care in<br />the details</div>
            <div className="story-illustration__cloth" />
            <div className="story-illustration__spray"><span /><i /></div>
            <div className="story-illustration__leaf story-illustration__leaf--a" />
            <div className="story-illustration__leaf story-illustration__leaf--b" />
          </div>
          <div className="story-copy">
            <p className="eyebrow eyebrow--light"><span />A more human approach</p>
            <h2>Cleaning that <i>fits</i><br />around living.</h2>
            <p>We think a good clean is less about a perfect-looking room and more about how it feels to come home. Share the areas that make the biggest difference, and we’ll start there.</p>
            <div className="story-points">
              <span><Check size={17} />Simple online planning</span>
              <span><Check size={17} />Priorities captured up front</span>
              <span><Check size={17} />Updates in one clear place</span>
            </div>
            <ArrowLink light to="/about">How Best One works</ArrowLink>
          </div>
        </div>
      </section>

      <section className="section section--paper how-section">
        <div className="page-shell">
          <SectionIntro
            align="center"
            eyebrow="Easy from first click"
            title={<>A clean plan in <i>three small steps.</i></>}
            copy="No calls to chase. Just a straightforward way to tell us what you need."
          />
          <div className="step-grid">
            <article className="step-card">
              <span className="step-card__number">01</span>
              <CalendarCheck2 size={28} />
              <h3>Pick your visit</h3>
              <p>Choose a service, home size and a time that works for your week.</p>
            </article>
            <article className="step-card">
              <span className="step-card__number">02</span>
              <ClipboardCheck size={28} />
              <h3>Share the details</h3>
              <p>Add notes, favourite products or the spots that matter most.</p>
            </article>
            <article className="step-card">
              <span className="step-card__number">03</span>
              <HeartHandshake size={28} />
              <h3>Come back to calm</h3>
              <p>We’ll confirm the plan before the visit, so you know what to expect.</p>
            </article>
          </div>
          <div className="how-section__cta"><Link className="button button--dark" to="/book">Build your clean plan <ArrowRight size={17} /></Link></div>
        </div>
      </section>

      <section className="cta-banner">
        <div className="page-shell cta-banner__inner">
          <div>
            <p className="eyebrow eyebrow--light"><span />A little room to breathe</p>
            <h2>Give your weekend<br /><i>back to yourself.</i></h2>
          </div>
          <Link className="button button--mint" to="/book">Find a time <ArrowRight size={17} /></Link>
        </div>
      </section>
    </>
  );
}

function BeforeAfterSlider() {
  const [position, setPosition] = useState(50);

  return (
    <figure className="home-before-after__figure">
      <div
        className="home-before-after__media"
        role="group"
        aria-label="Kitchen comparison, showing the room before and after a clean"
      >
        <div className="home-before-after__image home-before-after__image--before" style={{ backgroundImage: `url(${deepCleanBeforeAfter})` }} />
        <div
          className="home-before-after__image home-before-after__image--after"
          style={{ backgroundImage: `url(${deepCleanBeforeAfter})`, clipPath: `inset(0 0 0 ${position}%)` }}
        />
        <span className="home-before-after__label home-before-after__label--before">Before</span>
        <span className="home-before-after__label home-before-after__label--after">After</span>
        <span className="home-before-after__divider" style={{ left: `${position}%` }} aria-hidden="true" />
        <input
          aria-label="Move the divider to compare before and after"
          className="home-before-after__range"
          type="range"
          min="20"
          max="80"
          value={position}
          onChange={(event) => setPosition(Number(event.target.value))}
        />
      </div>
      <figcaption>Move the slider to compare both sides of this kitchen refresh.</figcaption>
    </figure>
  );
}

export function ServicesPage() {
  return (
    <>
      <section className="inner-hero inner-hero--moss">
        <div className="page-shell inner-hero__layout">
          <div>
            <p className="eyebrow eyebrow--light"><span />Our services</p>
            <h1>A clean for every<br /><i>kind of day.</i></h1>
          </div>
          <p>From regular maintenance to a move-day reset, choose the level of help that makes life feel a little lighter.</p>
        </div>
      </section>
      <section className="section section--cream">
        <div className="page-shell">
          <div className="service-grid service-grid--all">
            {services.map((service) => <ServiceCard key={service.id} service={service} />)}
          </div>
        </div>
      </section>
      <section className="section section--paper before-after-section">
        <div className="page-shell before-after-layout">
          <div className="before-after-copy">
            <p className="eyebrow"><span />Before & after</p>
            <h2>A fresh start,<br /><i>in the details.</i></h2>
            <p>Compare both sides of a kitchen refresh. Every home is different, and the visit is planned around the condition of your space.</p>
            <Link className="button button--outline" to="/pricing">Explore deep clean prices <ArrowRight size={16} /></Link>
          </div>
          <figure className="before-after-figure">
            <div className="before-after-media">
              <img src={deepCleanBeforeAfter} alt="A kitchen shown before and after a detailed clean." />
              <span className="before-after-label before-after-label--before">Before</span>
              <span className="before-after-label before-after-label--after">After</span>
            </div>
          </figure>
        </div>
      </section>
      <section className="section section--cream cleaning-scope-section">
        <div className="page-shell">
          <SectionIntro
            eyebrow="What’s included"
            title={<>Know what to expect<br /><i>from each visit.</i></>}
            copy="Choose a regular refresh, a more detailed deep clean, or a complete move-out clean."
          />
          <div className="cleaning-scope-grid">
            <article className="cleaning-scope-card">
              <p className="eyebrow"><span />Standard clean</p>
              <h3>For a home that’s regularly cared for.</h3>
              <ul>{standardCleanIncludes.map((item) => <li key={item}><Check size={16} />{item}</li>)}</ul>
              <Link className="arrow-link" to="/pricing">See standard clean rates <ArrowRight size={16} /></Link>
            </article>
            <article className="cleaning-scope-card cleaning-scope-card--tenancy">
              <p className="eyebrow"><span />End of tenancy</p>
              <h3>A room-by-room handover clean.</h3>
              <ul>{tenancyIncludes.map((item) => <li key={item}><Check size={16} />{item}</li>)}</ul>
              <Link className="arrow-link" to="/pricing">See end-of-tenancy rates <ArrowRight size={16} /></Link>
            </article>
          </div>
          <article className="deep-clean-scope-card">
            <div className="deep-clean-scope-card__heading">
              <p className="eyebrow"><span />Deep clean checklist</p>
              <h3>A thorough reset of your space.</h3>
              <p>Inside cupboards, interior windows, oven, microwave and fridge are included. An additional £15 may apply if the oven is heavily soiled.</p>
            </div>
            <div className="deep-clean-checklist">
              {deepCleanChecklist.map((group) => (
                <section key={group.title}>
                  <h4>{group.title}</h4>
                  <ul>{group.items.map((item) => <li key={item}><Check size={15} />{item}</li>)}</ul>
                </section>
              ))}
            </div>
            <div className="client-message-card">
              <div>
                <span>Copy-ready client message</span>
                <p>{deepCleanClientMessage}</p>
              </div>
              <CopyDeepCleanMessage />
            </div>
          </article>
        </div>
      </section>
      <section className="section section--paper">
        <div className="page-shell detail-band">
          <div><p className="eyebrow"><span />Need something tailored?</p><h2>Start with the closest service. <i>We’ll take it from there.</i></h2></div>
          <div><p>Every property has its own rhythm. Use the booking notes to explain what you need, or ask the team a question before you book.</p><div className="detail-band__links"><Link to="/book">Build a booking <ArrowRight size={16} /></Link><Link to="/contact">Contact the team <ArrowRight size={16} /></Link></div></div>
        </div>
      </section>
    </>
  );
}

function CopyDeepCleanMessage() {
  const [copied, setCopied] = useState(false);

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(deepCleanClientMessage);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button className="button button--outline client-message-card__button" type="button" onClick={copyMessage}>
      <ClipboardCheck size={17} />{copied ? "Copied" : "Copy message"}
    </button>
  );
}

export function PricingPage() {
  return (
    <>
      <section className="inner-hero inner-hero--sun">
        <div className="page-shell inner-hero__layout">
          <div>
            <p className="eyebrow"><span />Guide pricing</p>
            <h1>Clear prices<br /><i>for every clean.</i></h1>
          </div>
          <p>Choose the service and home size that best match your property. We’ll confirm the scope before the appointment.</p>
        </div>
      </section>
      <section className="section section--cream">
        <div className="page-shell price-rates">
          {(["regular", "deep", "tenancy"] as const).map((serviceId) => {
            const list = servicePriceLists[serviceId];
            const service = services.find((item) => item.id === serviceId)!;
            const Icon = service.icon;

            return (
              <section className="price-rate-card" key={serviceId}>
                <header className="price-rate-card__header">
                  <span className={`price-rate-card__icon price-rate-card__icon--${serviceId}`}><Icon size={22} /></span>
                  <div>
                    <h2>{list.title}</h2>
                    <p>{list.description}</p>
                  </div>
                  <Link className="button button--outline" to={`/book?service=${serviceId}`}>Book this clean <ArrowRight size={16} /></Link>
                </header>
                <div className="price-table-scroll">
                  <table className="price-table">
                    <thead><tr><th scope="col">Home size</th><th scope="col">Estimated time</th><th scope="col">Price</th></tr></thead>
                    <tbody>
                      {list.rates.map((rate) => (
                        <tr key={rate.id}>
                          <th scope="row">{rate.property}{rate.note && <small>{rate.note}</small>}</th>
                          <td>{rate.duration}</td>
                          <td>{formatMoney(rate.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {list.note && <p className="price-list-note">{list.note}</p>}
              </section>
            );
          })}
        </div>
      </section>
      <section className="section section--paper pricing-extras-section">
        <div className="page-shell">
          <SectionIntro eyebrow="Make it yours" title={<>Useful extras,<br /><i>when you need them.</i></>} copy="Add-on prices vary by service. Carpet cleaning depends on room size; tell us what you need when booking." />
          <div className="pricing-extra-groups">
            {pricingExtras.map((group) => (
              <article className="pricing-extra-group" key={group.title}>
                <h3>{group.title}</h3>
                <div className="pricing-extra-list">
                  {group.items.map((extra) => (
                    <div className="pricing-extra-row" key={extra.name}>
                      <div><b>{extra.name}</b>{extra.note && <small>{extra.note}</small>}</div>
                      <strong>{extra.price}</strong>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section section--light-moss">
        <div className="page-shell notes-grid">
          {pricingNotes.map((note, index) => <article key={note.title}><span>0{index + 1}</span><h3>{note.title}</h3><p>{note.copy}</p></article>)}
        </div>
      </section>
    </>
  );
}

export function AboutPage() {
  return (
    <>
      <section className="inner-hero inner-hero--ink">
        <div className="page-shell inner-hero__layout">
          <div>
            <p className="eyebrow eyebrow--light"><span />Why Best One</p>
            <h1>Better by the<br /><i>little things.</i></h1>
          </div>
          <p>A cleaning service should feel easy to arrange and genuinely useful to come home to. That simple idea shapes every part of Best One.</p>
        </div>
      </section>
      <section className="section section--cream">
        <div className="page-shell values-layout">
          <div className="values-art" aria-hidden="true"><div /><span>time<br />well<br />spent</span><i /><b /></div>
          <div className="values-copy">
            <SectionIntro eyebrow="The Best One difference" title={<>Helpful, clear, and<br /><i>built around you.</i></>} />
            <div className="value-list">
              <article><span><MessageCircle size={19} /></span><div><h3>Listen first</h3><p>You can share the rooms, routines and details that really change how your space feels.</p></div></article>
              <article><span><CreditCard size={19} /></span><div><h3>Keep it clear</h3><p>Our guide pricing gives you a useful starting point, followed by a confirmed estimate.</p></div></article>
              <article><span><Clock3 size={19} /></span><div><h3>Respect your time</h3><p>Booking, notes and visit details stay together, so you don’t have to repeat yourself.</p></div></article>
            </div>
          </div>
        </div>
      </section>
      <section className="section section--paper">
        <div className="page-shell team-callout">
          <div><p className="eyebrow"><span />Designed for the in-between moments</p><h2>Less time planning chores.<br /><i>More time in your life.</i></h2></div>
          <div><p>Whether you’re resetting after a busy week, getting ready to hand back keys, or keeping a guest space ready, we make it simpler to get the help you need.</p><Link className="button button--dark" to="/book">Start your booking <ArrowRight size={17} /></Link></div>
        </div>
      </section>
    </>
  );
}

type BookingForm = {
  serviceId: ServiceId;
  packageId: string;
  cadence: "weekly" | "fortnightly" | "monthly";
  date: string;
  time: string;
  selectedExtras: string[];
  name: string;
  email: string;
  postcode: string;
  notes: string;
};

const slots = ["09:00", "11:00", "13:30", "15:30"];

export function BookingPage() {
  const [searchParams] = useSearchParams();
  const requestedServiceId = searchParams.get("service");
  const initialServiceId: ServiceId = services.some((item) => item.id === requestedServiceId) ? requestedServiceId as ServiceId : "regular";
  const initialPackageId = servicePriceLists[initialServiceId].rates[0].id;
  const [step, setStep] = useState(1);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<LocalBookingRequest | null>(null);
  const [form, setForm] = useState<BookingForm>({
    serviceId: initialServiceId,
    packageId: initialPackageId,
    cadence: "weekly",
    date: "",
    time: "",
    selectedExtras: [],
    name: "",
    email: "",
    postcode: "",
    notes: "",
  });
  const service = services.find((item) => item.id === form.serviceId) ?? services[0];
  const priceList = servicePriceLists[form.serviceId];
  const selectedPackage = priceList.rates.find((item) => item.id === form.packageId) ?? priceList.rates[0];
  const availableExtras = form.serviceId === "regular" ? extras : [];
  const extrasTotal = form.selectedExtras.reduce((sum, id) => sum + (availableExtras.find((item) => item.id === id)?.price ?? 0), 0);
  const packagePrice = form.serviceId === "regular" && form.cadence === "monthly" ? 175 : selectedPackage.price;
  const estimate = packagePrice + extrasTotal;
  const today = new Date().toISOString().slice(0, 10);

  const toggleExtra = (id: string) => setForm((current) => ({
    ...current,
    selectedExtras: current.selectedExtras.includes(id)
      ? current.selectedExtras.filter((extra) => extra !== id)
      : id === "oven" || id === "double-oven"
        ? [...current.selectedExtras.filter((extra) => extra !== "oven" && extra !== "double-oven"), id]
        : [...current.selectedExtras, id],
  }));

  const continueBooking = () => {
    if (step === 3 && (!form.date || !form.time)) {
      setFormError("Choose a preferred date and arrival time to continue.");
      return;
    }
    setFormError(null);
    setStep((value) => value + 1);
  };

  const submitBooking = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.date || !form.time) {
      setFormError("Choose a preferred date and arrival time to continue.");
      setStep(3);
      return;
    }
    if (!form.name || !form.email || !form.postcode) {
      setFormError("Complete your contact details before saving the request.");
      return;
    }
    const booking: LocalBookingRequest = {
      id: `BOC-${Math.floor(1000 + Math.random() * 9000)}`,
      customer: form.name,
      email: form.email,
      service: form.serviceId === "regular" ? `${service.shortName} · ${form.cadence}` : service.shortName,
      date: form.date,
      time: form.time,
      total: estimate,
      status: "New",
      address: form.postcode,
    };
    if (!saveLocalBooking(booking)) {
      setFormError("Your browser could not save the request details. Check your browser storage and try again.");
      return;
    }
    setSubmitted(booking);
  };

  if (submitted) {

    return (
      <section className="booking-confirmation">
        <div className="page-shell booking-confirmation__card">
          <span className="confirmation-icon"><CircleCheck size={32} /></span>
          <p className="eyebrow"><span />Booking request saved</p>
          <h1>You’re on the<br /><i>right track.</i></h1>
          <p>Your request details are saved in this browser only and have not been sent to our team. Please contact us to arrange your visit.</p>
          <div className="confirmation-details"><span>{submitted.service}</span><span>{formatBookingDate(submitted.date)} · {submitted.time}</span><span>Guide total {formatMoney(submitted.total)}</span></div>
          <div className="hero__actions"><Link className="button button--dark" to="/contact">Contact the team <ArrowRight size={17} /></Link><Link className="button button--outline" to="/">Back home</Link></div>
        </div>
      </section>
    );
  }

  return (
    <section className="booking-page">
      <div className="page-shell booking-welcome-banner">
        <div>
          <p className="eyebrow"><span />Welcome to Best One Cleans</p>
          <p>Choose the service and home size that best fit your visit. If you are unsure, contact us before booking. Consultations should be booked at least 24 hours before the service is needed.</p>
        </div>
        <Link className="button button--outline" to="/contact">Ask us first <ArrowRight size={16} /></Link>
      </div>
      <div className="page-shell booking-layout">
        <aside className="booking-aside">
          <Link className="back-link" to="/"><ChevronRight size={15} />Back to home</Link>
          <p className="eyebrow eyebrow--light"><span />Plan your clean</p>
          <h1>A little more<br /><i>breathing room.</i></h1>
          <p>Tell us the essentials first. You’ll see a guide estimate before you share your contact details.</p>
          <div className="booking-aside__steps">
            {["Your service", "Your space", "Time & extras", "Your details"].map((label, index) => <div key={label} className={step === index + 1 ? "is-current" : step > index + 1 ? "is-done" : ""}><span>{step > index + 1 ? <Check size={14} /> : `0${index + 1}`}</span>{label}</div>)}
          </div>
          <div className="booking-aside__estimate" aria-live="polite"><span>Current guide</span><strong>{formatMoney(estimate)}</strong><small>Updates as you plan</small></div>
          <div className="booking-aside__note"><ShieldCheck size={17} />Your request details are stored only in this browser and are not sent to the team.</div>
        </aside>

        <form className="booking-form" onSubmit={submitBooking}>
          <div className="booking-form__top"><span>Step {step} of 4</span><div className="progress-track"><i style={{ width: `${step * 25}%` }} /></div></div>
          {step === 1 && <BookingServiceStep form={form} setForm={setForm} />}
          {step === 2 && <BookingHomeStep form={form} setForm={setForm} />}
          {step === 3 && <BookingVisitStep form={form} setForm={setForm} toggleExtra={toggleExtra} today={today} serviceId={form.serviceId} />}
          {step === 4 && <BookingDetailsStep form={form} setForm={setForm} />}
          {formError && <p className="booking-error" role="alert">{formError}</p>}
          <div className="booking-form__footer">
            <div className="estimate"><span>Guide estimate</span><strong>{formatMoney(estimate)}</strong><small>Final scope confirmed before acceptance</small></div>
            <div className="booking-form__buttons">
              {step > 1 && <button className="button button--outline" type="button" onClick={() => setStep((value) => value - 1)}>Back</button>}
              {step < 4 ? <button className="button button--dark" type="button" onClick={continueBooking}>Continue <ArrowRight size={17} /></button> : <button className="button button--dark" type="submit">Save booking request <ArrowRight size={17} /></button>}
            </div>
          </div>
        </form>
      </div>
      <div className="page-shell booking-policy-card">
        <div><p className="eyebrow"><span />Appointment changes</p><h2>Plans change. Just let us know.</h2></div>
        <p>Please give 24–48 hours’ notice to cancel or reschedule. Include your original appointment and the time you would prefer instead. If an appointment is not cancelled or rescheduled and we receive no response, the full service charge will apply.</p>
      </div>
    </section>
  );
}

function BookingServiceStep({ form, setForm }: { form: BookingForm; setForm: React.Dispatch<React.SetStateAction<BookingForm>> }) {
  return <div className="booking-step"><div className="booking-step__intro"><p className="eyebrow"><span />First things first</p><h2>What would make the biggest difference?</h2><p>Choose the clean that is closest to what your space needs.</p></div><div className="booking-service-options">{services.map((item) => { const Icon = item.icon; return <button aria-pressed={form.serviceId === item.id} className={`service-option ${form.serviceId === item.id ? "is-selected" : ""}`} type="button" onClick={() => setForm((current) => ({ ...current, serviceId: item.id, packageId: servicePriceLists[item.id].rates[0].id, selectedExtras: [] }))} key={item.id}><span><Icon size={21} /></span><b>{item.shortName}</b><small>from {formatMoney(item.startingAt)}</small><i>{form.serviceId === item.id && <Check size={16} />}</i></button>; })}</div></div>;
}

function BookingHomeStep({ form, setForm }: { form: BookingForm; setForm: React.Dispatch<React.SetStateAction<BookingForm>> }) {
  const packages = servicePriceLists[form.serviceId].rates;
  const selectedPackage = packages.find((item) => item.id === form.packageId) ?? packages[0];

  return (
    <div className="booking-step">
      <div className="booking-step__intro"><p className="eyebrow"><span />Your space</p><h2>Choose your home size.</h2><p>Select the package closest to your property to see the matching guide price.</p></div>
      <label className="field-label package-select-label">Property size and bathroom count
        <select className="package-select" value={selectedPackage.id} onChange={(event) => setForm((current) => ({ ...current, packageId: event.target.value }))}>
          {packages.map((item) => <option key={item.id} value={item.id}>{item.property} — {formatMoney(item.price)}</option>)}
        </select>
      </label>
      <p className="selected-package-summary"><Clock3 size={16} />{form.serviceId === "regular" && form.cadence === "monthly" ? <>Monthly clean package · {formatMoney(175)}</> : <>{selectedPackage.duration}<span>·</span>{formatMoney(selectedPackage.price)}</>}</p>
      {form.serviceId === "regular" ? (
        <fieldset className="cadence-field"><legend>How often would you like this clean?</legend><div>{(["weekly", "fortnightly", "monthly"] as const).map((cadence) => <label key={cadence}><input type="radio" name="cadence" checked={form.cadence === cadence} onChange={() => setForm((current) => ({ ...current, cadence }))} /><span>{cadence === "weekly" ? "Weekly" : cadence === "fortnightly" ? "Fortnightly" : "Monthly"}</span></label>)}</div></fieldset>
      ) : <p className="booking-step-note">This service is booked as a one-off visit.</p>}
    </div>
  );
}

function BookingVisitStep({ form, setForm, toggleExtra, today, serviceId }: { form: BookingForm; setForm: React.Dispatch<React.SetStateAction<BookingForm>>; toggleExtra: (id: string) => void; today: string; serviceId: ServiceId }) {
  const bookingExtras = serviceId === "regular" ? extras : [];
  const extraNote = serviceId === "deep"
    ? "Deep clean includes oven, fridge and cupboards. A heavily soiled oven adds £15; carpet cleaning is £25 per room. Add these details to your notes."
    : serviceId === "tenancy"
      ? "Inside appliances and cupboards are included. Carpet cleaning is £25 per room; include the number of rooms in your notes."
      : "Carpet cleaning is £40 per room, depending on room size. Add the number of rooms in your notes.";

  return <div className="booking-step"><div className="booking-step__intro"><p className="eyebrow"><span />Visit details</p><h2>When should we make the space shine?</h2><p>Choose a preferred visit time and any useful extras.</p></div><div className="date-time-grid"><label className="field-label">Preferred date<input required min={today} type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} /></label><div className="field-label"><span>Preferred arrival</span><div className="time-options">{slots.map((slot) => <button type="button" aria-pressed={form.time === slot} className={form.time === slot ? "is-selected" : ""} onClick={() => setForm((current) => ({ ...current, time: slot }))} key={slot}>{slot}</button>)}</div></div></div>{bookingExtras.length > 0 ? <div className="extras-options"><p>Standard clean extras <small>optional</small></p>{bookingExtras.map((extra) => <label key={extra.id}><input type="checkbox" checked={form.selectedExtras.includes(extra.id)} onChange={() => toggleExtra(extra.id)} /><span><b>{extra.name}</b><small>+ {formatMoney(extra.price)}</small></span><i>{form.selectedExtras.includes(extra.id) && <Check size={15} />}</i></label>)}<p className="booking-step-note">{extraNote}</p></div> : <p className="booking-step-note">{extraNote}</p>}</div>;
}

function BookingDetailsStep({ form, setForm }: { form: BookingForm; setForm: React.Dispatch<React.SetStateAction<BookingForm>> }) {
  return <div className="booking-step"><div className="booking-step__intro"><p className="eyebrow"><span />Last step</p><h2>Where can we reach you?</h2><p>Your request details are saved on this device only and are not sent to the team.</p></div><div className="contact-fields"><label className="field-label">Your name<input autoComplete="name" required value={form.name} placeholder="e.g. Alex Morgan" onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></label><label className="field-label">Email address<input autoComplete="email" required type="email" value={form.email} placeholder="alex@example.com" onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} /></label><label className="field-label">Postcode<input autoComplete="postal-code" required value={form.postcode} placeholder="e.g. SW1A 1AA" onChange={(event) => setForm((current) => ({ ...current, postcode: event.target.value }))} /></label><label className="field-label field-label--wide">Anything we should know?<textarea value={form.notes} placeholder="Access notes, favourite products, areas to focus on…" onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></label></div></div>;
}

export function ContactPage() {
  const [sent, setSent] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); setSent(true); };
  return <><section className="inner-hero inner-hero--sky"><div className="page-shell inner-hero__layout"><div><p className="eyebrow"><span />Contact us</p><h1>Let’s make it<br /><i>easy.</i></h1></div><p>Have a question about a service or an upcoming visit? Leave a note and we’ll point you in the right direction.</p></div></section><section className="section section--cream"><div className="page-shell contact-layout"><div className="contact-aside"><p className="eyebrow"><span />Before you book</p><h2>Start with the<br /><i>important bit.</i></h2><p>Use the form for general questions. If you’re ready to plan a visit, the booking flow gives you a clearer starting estimate.</p><div className="contact-aside__links"><Link to="/faq"><MessageCircle size={18} />Read common questions <ArrowRight size={16} /></Link><Link to="/book"><CalendarCheck2 size={18} />Build a booking <ArrowRight size={16} /></Link></div></div><form className="contact-form" onSubmit={submit}>{sent ? <div className="form-success"><AlertCircle size={35} /><h2>Your message has not been sent.</h2><p>This form is not connected to the team’s inbox. The text you entered was not saved or sent.</p><button type="button" className="button button--outline" onClick={() => setSent(false)}>Return to contact form</button></div> : <><div className="contact-fields"><label className="field-label">Name<input autoComplete="name" required placeholder="Your name" /></label><label className="field-label">Email<input autoComplete="email" required type="email" placeholder="you@example.com" /></label><label className="field-label field-label--wide">What can we help with?<textarea required placeholder="Tell us a little about your question…" /></label></div><button className="button button--dark" type="submit">Send a message <ArrowRight size={17} /></button></>}</form></div></section></>;
}

export function FaqPage() {
  const [open, setOpen] = useState(0);
  return <><section className="inner-hero inner-hero--paper"><div className="page-shell inner-hero__layout"><div><p className="eyebrow"><span />Questions, answered</p><h1>The useful<br /><i>small print.</i></h1></div><p>Everything you might want to know before you plan a cleaning visit.</p></div></section><section className="section section--cream"><div className="page-shell faq-layout"><div><SectionIntro eyebrow="Good to know" title={<>A little more<br /><i>clarity.</i></>} copy="Can’t find what you need? Send the team a note and we’ll help." /><Link className="button button--outline" to="/contact">Ask a question <ArrowRight size={16} /></Link></div><div className="faq-list">{faqs.map((faq, index) => { const panelId = `faq-panel-${index}`; const buttonId = `faq-button-${index}`; return <article className={open === index ? "is-open" : ""} key={faq.question}><button id={buttonId} aria-controls={panelId} onClick={() => setOpen((value) => value === index ? -1 : index)} aria-expanded={open === index}><span>{faq.question}</span>{open === index ? <Minus size={19} /> : <Plus size={19} />}</button>{open === index && <p id={panelId} role="region" aria-labelledby={buttonId}>{faq.answer}</p>}</article>; })}</div></div></section></>;
}

type DashboardTab = "overview" | "bookings" | "services" | "customers";

type AdminUser = {
  is_staff: boolean;
};

type BookingStatus = "pending" | "confirmed" | "assigned" | "in_progress" | "completed" | "cancelled";

type AdminBooking = {
  id: number;
  customer: { id: number; display_name: string };
  service_details: { name: string };
  scheduled_for: string;
  start_time: string;
  quoted_price: string;
  status: BookingStatus;
};

type BookingPageResponse = {
  next: string | null;
  results: AdminBooking[];
};

type ApiErrorResponse = {
  detail?: string;
  non_field_errors?: string[];
};

const adminTokenKey = "best-one-cleans-admin-token";

class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function readApiResponse<T>(response: Response): Promise<T> {
  let result: T | ApiErrorResponse | null = null;
  try {
    result = await response.json() as T | ApiErrorResponse;
  } catch {
    if (response.ok) throw new Error("The server returned an unreadable response.");
  }
  if (!response.ok) {
    const error = result as ApiErrorResponse | null;
    throw new ApiError(
      error?.detail ?? error?.non_field_errors?.[0] ?? `The request failed (${response.status}).`,
      response.status,
    );
  }
  if (result === null) throw new Error("The server returned an empty response.");
  return result as T;
}

async function fetchAdminBookings(token: string, signal: AbortSignal): Promise<AdminBooking[]> {
  const headers = { Authorization: `Bearer ${token}` };
  let page = await readApiResponse<BookingPageResponse>(
    await fetch("/api/bookings/", { headers, signal }),
  );
  const bookings = [...page.results];
  while (page.next) {
    const nextPage = new URL(page.next, window.location.origin);
    if (nextPage.origin !== window.location.origin) {
      throw new Error("The bookings API returned an unexpected page address.");
    }
    page = await readApiResponse<BookingPageResponse>(
      await fetch(nextPage, { headers, signal }),
    );
    bookings.push(...page.results);
  }
  return bookings;
}

export function DashboardPage() {
  const [token, setToken] = useState(() => window.sessionStorage.getItem(adminTokenKey));
  const [authStatus, setAuthStatus] = useState<"checking" | "anonymous" | "admin" | "forbidden">("checking");
  const [authError, setAuthError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [activeTab, setActiveTab] = useState<DashboardTab>("overview");
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");

  useEffect(() => {
    if (!token) {
      setAuthStatus("anonymous");
      return undefined;
    }
    const controller = new AbortController();
    setAuthStatus("checking");
    setAuthError("");
    fetch("/api/auth/me/", {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((response) => readApiResponse<AdminUser>(response))
      .then((user) => {
        if (user.is_staff) {
          setAuthStatus("admin");
          return;
        }
        window.sessionStorage.removeItem(adminTokenKey);
        setAuthStatus("forbidden");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        window.sessionStorage.removeItem(adminTokenKey);
        setToken(null);
        setAuthStatus("anonymous");
        setAuthError(error instanceof Error ? error.message : "We could not verify your account.");
      });
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (authStatus !== "admin" || !token) return undefined;
    const controller = new AbortController();
    setBookingsLoading(true);
    setDashboardError("");
    fetchAdminBookings(token, controller.signal)
      .then(setBookings)
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setDashboardError(error instanceof Error ? error.message : "Bookings could not be loaded.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setBookingsLoading(false);
      });
    return () => controller.abort();
  }, [authStatus, token]);

  const customerCount = useMemo(() => new Set(bookings.map((booking) => booking.customer.id)).size, [bookings]);
  const upcomingBookings = bookings.filter((booking) => booking.scheduled_for >= new Date().toISOString().slice(0, 10)
    && booking.status !== "cancelled"
    && booking.status !== "completed");
  const revenue = bookings
    .filter((booking) => booking.status !== "cancelled")
    .reduce((sum, booking) => sum + Number(booking.quoted_price), 0);

  const signIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSigningIn(true);
    setAuthError("");
    try {
      const response = await fetch("/api/auth/token/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const result = await readApiResponse<{ access: string }>(response);
      window.sessionStorage.setItem(adminTokenKey, result.access);
      setToken(result.access);
      setPassword("");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "Sign-in failed. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const signOut = () => {
    window.sessionStorage.removeItem(adminTokenKey);
    setToken(null);
    setBookings([]);
    setAuthStatus("anonymous");
    setAuthError("");
    setDashboardError("");
  };

  const updateStatus = async (id: number, status: BookingStatus) => {
    if (!token) return;
    setDashboardError("");
    try {
      const response = await fetch(`/api/bookings/${id}/`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const updated = await readApiResponse<AdminBooking>(response);
      setBookings((current) => current.map((booking) => booking.id === id ? updated : booking));
    } catch (error) {
      setDashboardError(error instanceof Error ? error.message : "The booking status could not be updated.");
    }
  };

  if (authStatus === "checking") {
    return <section className="dashboard-page dashboard-access"><div className="dashboard-access__card"><LoaderCircle className="dashboard-access__spinner" size={26} /><p>Checking administrator access…</p></div></section>;
  }

  if (authStatus === "forbidden") {
    return <section className="dashboard-page dashboard-access"><div className="dashboard-access__card"><span className="dashboard-access__icon"><LockKeyhole size={23} /></span><p className="eyebrow"><span />Restricted area</p><h1>Administrator access only.</h1><p>This account does not have permission to view the dashboard.</p><button className="button button--dark" onClick={signOut}>Sign in with another account</button></div></section>;
  }

  if (authStatus === "anonymous") {
    return (
      <section className="dashboard-page dashboard-access">
        <div className="dashboard-access__card">
          <span className="dashboard-access__icon"><LockKeyhole size={23} /></span>
          <p className="eyebrow"><span />Administrator sign in</p>
          <h1>Welcome back.</h1>
          <p>Sign in with an administrator account to manage bookings and customers.</p>
          <form className="admin-login-form" onSubmit={signIn}>
            <label className="field-label">Username<input required autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} /></label>
            <label className="field-label">Password<input required type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
            {authError && <p className="dashboard-error" role="alert"><AlertCircle size={16} />{authError}</p>}
            <button className="button button--dark" type="submit" disabled={signingIn}>{signingIn ? "Signing in…" : "Sign in"} <ArrowRight size={17} /></button>
          </form>
          <Link className="dashboard-access__back" to="/">Back to the public site</Link>
        </div>
      </section>
    );
  }

  const displayedBookings = activeTab === "overview" ? upcomingBookings.slice(0, 4) : bookings;
  return (
    <section className="dashboard-page">
      <div className="page-shell dashboard-shell">
        <aside className="dashboard-sidebar">
          <Link className="brand" to="/"><span className="brand__mark"><Sparkles size={16} /></span><span>best<span>one</span><em>cleans</em></span></Link>
          <p className="dashboard-sidebar__label">Workspace</p>
          <nav>{([["overview", LayoutDashboard, "Overview"], ["bookings", CalendarCheck2, "Bookings"], ["services", Sparkles, "Services"], ["customers", Users, "Customers"]] as const).map(([id, Icon, label]) => <button key={id} onClick={() => setActiveTab(id)} className={activeTab === id ? "is-active" : ""}><Icon size={18} />{label}{id === "bookings" && <span>{bookings.length}</span>}</button>)}</nav>
          <div className="dashboard-sidebar__bottom">
            <Link to="/">View public site <ArrowUpRight size={15} /></Link>
            <button className="dashboard-signout" onClick={signOut}><LogOut size={15} />Sign out</button>
            <p>Administrator workspace</p>
          </div>
        </aside>
        <div className="dashboard-content">
          <header className="dashboard-topbar">
            <div><p className="eyebrow"><span />Administrator workspace</p><h1>{activeTab === "overview" ? "Overview" : activeTab[0].toUpperCase() + activeTab.slice(1)}</h1></div>
            <Link className="button button--dark" to="/book">New booking <Plus size={17} /></Link>
          </header>
          {dashboardError && <p className="dashboard-error" role="alert"><AlertCircle size={16} />{dashboardError}</p>}
          {activeTab === "overview" && <>
            <div className="metric-grid">
              <DashboardMetric label="Booking value" value={formatMoney(revenue)} note="Excludes cancelled bookings" icon={<CalendarCheck2 size={20} />} />
              <DashboardMetric label="Bookings" value={String(bookings.length).padStart(2, "0")} note="All recorded bookings" icon={<CalendarCheck2 size={20} />} />
              <DashboardMetric label="Customers" value={String(customerCount).padStart(2, "0")} note="Unique booking customers" icon={<Users size={20} />} />
            </div>
            <div className="dashboard-section-heading">
              <div><h2>Upcoming bookings</h2><p>Visits scheduled from today onwards.</p></div>
              <button onClick={() => setActiveTab("bookings")}>View all <ArrowRight size={16} /></button>
            </div>
            {bookingsLoading ? <DashboardLoading /> : <BookingTable bookings={displayedBookings} onStatusChange={updateStatus} />}
          </>}
          {activeTab === "bookings" && <>
            <div className="dashboard-section-heading dashboard-section-heading--solo"><div><h2>All bookings</h2><p>View and update scheduled visits.</p></div></div>
            {bookingsLoading ? <DashboardLoading /> : <BookingTable bookings={displayedBookings} onStatusChange={updateStatus} />}
          </>}
          {activeTab === "services" && <ServiceAdmin />}
          {activeTab === "customers" && <CustomerAdmin bookings={bookings} />}
        </div>
      </div>
    </section>
  );
}

function DashboardLoading() {
  return <div className="dashboard-empty"><LoaderCircle className="dashboard-access__spinner" size={22} />Loading records…</div>;
}

function DashboardMetric({ label, value, note, icon }: { label: string; value: string; note: string; icon: React.ReactNode }) {
  return <article className="metric-card"><span>{icon}</span><p>{label}</p><h2>{value}</h2><small>{note}</small></article>;
}

function BookingTable({ bookings, onStatusChange }: { bookings: AdminBooking[]; onStatusChange: (id: number, status: BookingStatus) => void }) {
  if (!bookings.length) return <div className="dashboard-empty">No bookings to display yet.</div>;
  const statuses: BookingStatus[] = ["pending", "confirmed", "assigned", "in_progress", "completed", "cancelled"];
  return <div className="booking-table-wrap"><table className="booking-table"><thead><tr><th>Customer</th><th>Service</th><th>Visit</th><th>Estimate</th><th>Status</th></tr></thead><tbody>{bookings.map((booking) => <tr key={booking.id}><td data-label="Customer"><b>{booking.customer.display_name}</b><small>Booking #{booking.id}</small></td><td data-label="Service">{booking.service_details.name}</td><td data-label="Visit"><b>{formatBookingDate(booking.scheduled_for)}</b><small>{booking.start_time.slice(0, 5)}</small></td><td data-label="Estimate">{formatMoney(Number(booking.quoted_price))}</td><td data-label="Status"><select aria-label={`Update status for booking ${booking.id}`} value={booking.status} onChange={(event) => onStatusChange(booking.id, event.target.value as BookingStatus)} className={`status-select status-select--${booking.status.replace("_", "-")}`}>{statuses.map((status) => <option key={status} value={status}>{status.replace("_", " ")}</option>)}</select></td></tr>)}</tbody></table></div>;
}

function ServiceAdmin() {
  return <div className="admin-card-grid">{services.map((service) => { const Icon = service.icon; return <article key={service.id} className="admin-service-card"><span><Icon size={20} /></span><div><h3>{service.shortName}</h3><p>{service.duration}</p></div><b>from {formatMoney(service.startingAt)}</b><span className="admin-service-card__status">Available</span></article>; })}</div>;
}

function CustomerAdmin({ bookings }: { bookings: AdminBooking[] }) {
  const customers = Array.from(new Map(bookings.map((booking) => [booking.customer.id, booking.customer])).values());
  if (!customers.length) return <div className="dashboard-empty">Customers will appear here when bookings are recorded.</div>;
  return <div className="customer-list">{customers.map((customer) => <article key={customer.id}><span>{customer.display_name.slice(0, 1)}</span><div><h3>{customer.display_name}</h3><p>Customer #{customer.id}</p></div><small>{bookings.filter((booking) => booking.customer.id === customer.id).length} booking(s)</small></article>)}</div>;
}

export function NotFoundPage() {
  return <section className="not-found"><div className="page-shell"><p className="eyebrow"><span />404</p><h1>This page needs<br /><i>a quick reset.</i></h1><p>Let’s get you back to something useful.</p><Link className="button button--dark" to="/">Back home <ArrowRight size={17} /></Link></div></section>;
}
